import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Sticky } from '#app/components/layout/sticky.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Code } from '#app/old_components/typography/code.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Sticky',
}

const component: componentProps = {
	name: 'Sticky',
	shortName: 'Sticky',
	fileName: 'sticky',
	category: 'layout',
	description: ['Sticky allows an element to become fixed in its parent container when it reaches a top, left, bottom, or right threshold.'],
	parts: [
		{
			name: 'Sticky',
			description: 'Sticky allows an element to become fixed in its parent container when it reaches a top, left, bottom, or right threshold.',
		},
	],
	usage: `<div className="mt-14 h-80 overflow-y-auto rounded-lg border">
	<div className="h-10" />
	<Sticky className="top-0 bg-green-500">This sticks</Sticky>
	<div className="h-40" />
	<p>Some content</p>
	<div className="h-40" />
	<Sticky className="top-0 bg-emerald-500">This also sticks</Sticky>
	<div className="h-40" />
	<p>Some more content</p>
	<div className="h-40" />
</div>`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const StickyRoute = () => {
	const { source } = useLoaderData<typeof loader>()

	const { name, description, shortName, fileName, features, usage, parts } = component

	return (
		<>
			<Description name={name} description={description} />
			<P className="mt-4">
				You are responsible for giving it a <Code>top-x</Code>, <Code>left-x</Code>, <Code>bottom-x</Code> or <Code>right-x</Code> position to stick to.
			</P>
			<Features features={features} />
			<Parts parts={parts} />
			<Usage usage={usage} shortName={shortName} />
			<div className="mt-14 h-80 overflow-y-auto rounded-lg border">
				<div className="h-10" />
				<Sticky className="top-0 bg-green-500">This sticks</Sticky>
				<div className="h-40" />
				<p>Some content</p>
				<div className="h-40" />
				<Sticky className="top-0 bg-emerald-500">This also sticks</Sticky>
				<div className="h-40" />
				<p>Some more content</p>
				<div className="h-40" />
			</div>
			<Source source={source} fileName={fileName} />
		</>
	)
}
export default StickyRoute
